import { ChapterCache, gameState, type Action, type ApplyResult, type BotUser, type GameEvent, type GameState, type GlobalDocs, type Lang } from '@fakerpg/core';
import type { AccountView, LoginNotice, ServerApi } from './api.js';
import { defaultPrefs, type Prefs, type PrefsStore } from './prefs.js';

export type ToastIcon = 'level' | 'coin' | 'loot' | 'gift' | 'sword' | 'trophy' | 'energy' | 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  tone: 'info' | 'success' | 'error';
  text: string;
  icon?: ToastIcon;
}

/**
 * Where the client stands with the server: `loading` (first fetch), `ready`
 * (the account is shown), `signedOut` (login screen), `noAccount` (no bot
 * account and a read-only server), `offline` / `error` (nothing loaded yet).
 */
export type Phase = 'loading' | 'ready' | 'signedOut' | 'noAccount' | 'offline' | 'error';

export interface Snapshot {
  phase: Phase;
  /** Why the login screen is shown (`?login=` from the server, or an ended session). */
  notice: LoginNotice | null;
  /** The bot account and the session's in-progress interactions, as the server sent them. */
  state: GameState | null;
  /** Shared documents from the server (the bet odds). */
  globals: Partial<GlobalDocs>;
  /** The server accepts game actions (`WEB_WRITES`); false: view only. */
  writes: boolean;
  /** An action is on its way to the server. */
  busy: boolean;
  prefs: Prefs;
  /** Events of the last successful dispatch (for screen feedback). */
  lastEvents: GameEvent[];
  toasts: Toast[];
  version: number;
}

/**
 * Runs a game action locally — UI tests only (`test/store.ts`), to exercise
 * the screens without a server. The product sends every action to the
 * server (`POST /api/action`, T3.8).
 */
export type ActionRunner = (state: GameState, action: Action, ctx: { now: number; content: ChapterCache; globals: Partial<GlobalDocs> }) => ApplyResult;

export type StoreMessage = 'account.created' | 'actions.readOnly' | 'actions.tooFast' | 'actions.offline' | 'actions.failed';

type ToastFormatter = (event: GameEvent, user: BotUser, lang: Lang) => { text: string; icon: ToastIcon } | null;

/** A fresh idempotency key per action (the server ignores a repeated delivery). */
function idempotencyKey(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = c?.getRandomValues ? c.getRandomValues(new Uint8Array(16)) : Uint8Array.from({ length: 16 }, () => (performance.now() * 997) & 255);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * External store over the server's account view (05-architecture §4). The
 * account is read (T3.7) and changed (T3.8) on the server only: the browser
 * never computes or saves game state (ADR-016).
 */
export class GameStore {
  readonly content = new ChapterCache();
  private snapshot: Snapshot;
  private readonly listeners = new Set<() => void>();
  private toastSeq = 0;
  formatToast: ToastFormatter = () => null;
  formatError: (code: string, params: Record<string, number | string>, lang: Lang) => string = (code) => code;
  /** Store-level texts (`account.created`, `actions.soon`), set by the app with the i18n table. */
  formatMessage: (key: StoreMessage, lang: Lang) => string = (key) => key;

  private loading: Promise<void> | null = null;
  /** Actions go to the server one after the other, in click order. */
  private queue: Promise<unknown> = Promise.resolve();
  /** Actions queued or on their way, by content: a double click sends one (soak hardening). */
  private readonly pending = new Set<string>();
  /** Bumped by every action sent: an account read that overlaps one is stale. */
  private actionSeq = 0;

  constructor(
    private readonly api: ServerApi,
    private readonly prefsStore: PrefsStore,
    private readonly clock: () => number = () => Date.now(),
    fallbackLang: Lang = 'fr',
    notice: LoginNotice | null = null,
    private readonly runner: ActionRunner | null = null,
  ) {
    const prefs = { ...defaultPrefs(fallbackLang), ...(prefsStore.load() ?? {}) };
    this.snapshot = { phase: 'loading', notice, state: null, globals: {}, writes: false, busy: false, prefs, lastEvents: [], toasts: [], version: 0 };
  }

  /** False on a read-only server: screens show actions as unavailable. */
  get actionsEnabled(): boolean {
    return this.runner !== null || this.snapshot.writes;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): Snapshot => this.snapshot;

  now(): number {
    return this.clock();
  }

  /** The shared documents from the last load (the bet odds on the Games screen). */
  globals(): Partial<GlobalDocs> {
    return this.snapshot.globals;
  }

  private set(patch: Partial<Snapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch, version: this.snapshot.version + 1 };
    for (const l of this.listeners) l();
  }

  /**
   * Loads (or reloads) the account from the server. With an account on
   * screen, a network failure or a server error keeps it (the offline strip
   * says why); a 401 means the session ended: back to the login screen.
   * Concurrent calls share one request.
   */
  load(): Promise<void> {
    this.loading ??= this.fetchAccount().finally(() => {
      this.loading = null;
    });
    return this.loading;
  }

  private async fetchAccount(): Promise<void> {
    const shown = this.snapshot.phase === 'ready';
    const seq = this.actionSeq;
    const result = await this.api.account();
    switch (result.kind) {
      case 'ok':
        // An action answered while this read was on its way: its account is
        // newer than the one read here (soak: a refresh must not roll it back).
        if (shown && seq !== this.actionSeq) return;
        this.show(result.view);
        return;
      case 'signedOut':
        this.set({ phase: 'signedOut', notice: shown ? 'session' : this.snapshot.notice, state: null, lastEvents: [], toasts: [] });
        return;
      case 'noAccount':
        this.set({ phase: 'noAccount', state: null });
        return;
      case 'offline':
      case 'error':
        if (!shown) this.set({ phase: result.kind });
        return;
    }
  }

  /** Shows an account view from the server. */
  show(view: AccountView): void {
    this.set({ phase: 'ready', notice: null, state: gameState(view.user, view.session), globals: view.globals, writes: view.writes });
    if (view.created) this.toast('success', this.formatMessage('account.created', this.snapshot.prefs.lang), 'gift');
  }

  /** Signs out on the server, then shows the login screen. */
  async logout(): Promise<void> {
    await this.api.logout();
    this.set({ phase: 'signedOut', notice: null, state: null, globals: {}, lastEvents: [], toasts: [] });
  }

  setPrefs(patch: Partial<Prefs>): void {
    const prefs = { ...this.snapshot.prefs, ...patch };
    this.prefsStore.save(prefs);
    this.set({ prefs });
  }

  /**
   * Sends a game action to the server (one Firestore transaction, T3.8) and
   * shows the account it answers with. Resolves to the result — `null` when
   * nothing happened (signed out, read-only, offline…), with an `error` when
   * a game rule refused it. Actions are queued so they reach the server in
   * click order. `silent` mutes error notices (background calls); rewards
   * are always announced.
   */
  dispatch(action: Action, opts: { silent?: boolean } = {}): Promise<ApplyResult | null> {
    // The same action already queued or in flight (a double click, a key held
    // down): this one is dropped. Different actions still queue in order.
    const key = JSON.stringify(action);
    if (this.pending.has(key)) return Promise.resolve(null);
    this.pending.add(key);
    const next = this.queue.then(() => this.send(action, opts)).finally(() => this.pending.delete(key));
    this.queue = next.catch(() => undefined);
    return next;
  }

  private async send(action: Action, opts: { silent?: boolean }): Promise<ApplyResult | null> {
    const current = this.snapshot.state;
    if (!current) return null;
    const lang = this.snapshot.prefs.lang;
    const notify = (key: StoreMessage) => {
      const text = this.formatMessage(key, lang);
      if (!opts.silent && !this.snapshot.toasts.some((t) => t.text === text)) this.toast('info', text, 'info');
    };
    if (this.runner) {
      const result = this.runner(current, action, { now: this.clock(), content: this.content, globals: this.snapshot.globals });
      return this.settle(result, opts, lang);
    }
    if (!this.snapshot.writes) {
      notify('actions.readOnly');
      return null;
    }
    this.actionSeq++;
    this.set({ busy: true });
    const r = await this.api.action(action, idempotencyKey());
    this.actionSeq++;
    this.set({ busy: false });
    switch (r.kind) {
      case 'ok':
        return this.settle({ state: gameState(r.view.user, r.view.session), events: r.events }, opts, lang, r.view.globals);
      case 'refused': {
        // The rule was checked on the stored account, which the bot may have
        // changed since this page read it (spent lootcoins on Discord…):
        // reload it so the screen shows why, after the actions already queued.
        this.queue = this.queue.then(() => this.load()).catch(() => undefined);
        const error = { code: r.code as NonNullable<ApplyResult['error']>['code'], params: r.params };
        return this.settle({ state: current, events: [], error }, opts, lang);
      }
      case 'signedOut':
        this.set({ phase: 'signedOut', notice: 'session', state: null, lastEvents: [], toasts: [] });
        return null;
      case 'readOnly':
        this.set({ writes: false });
        notify('actions.readOnly');
        return null;
      case 'tooFast':
        notify('actions.tooFast');
        return null;
      case 'offline':
        notify('actions.offline');
        return null;
      case 'error':
        notify('actions.failed');
        return null;
    }
  }

  /** Shows an action's outcome: the new state and reward toasts, or the refusal. */
  private settle(result: ApplyResult, opts: { silent?: boolean }, lang: Lang, globals?: Partial<GlobalDocs>): ApplyResult {
    if (result.error) {
      if (!opts.silent) this.toast('error', this.formatError(result.error.code, result.error.params, lang));
      return result;
    }
    const fresh = result.events
      .map((e) => this.formatToast(e, result.state.user, lang))
      .filter((t): t is { text: string; icon: ToastIcon } => !!t)
      .map((t) => ({ id: ++this.toastSeq, tone: 'success' as const, text: t.text, icon: t.icon }));
    const toasts = fresh.length ? [...this.snapshot.toasts, ...fresh].slice(-4) : this.snapshot.toasts;
    this.set({ state: result.state, lastEvents: result.events, toasts, ...(globals ? { globals } : {}) });
    return result;
  }

  toast(tone: Toast['tone'], text: string, icon?: ToastIcon): void {
    this.set({ toasts: [...this.snapshot.toasts, { id: ++this.toastSeq, tone, text, ...(icon ? { icon } : {}) }].slice(-4) });
  }

  dismissToast(id: number): void {
    this.set({ toasts: this.snapshot.toasts.filter((t) => t.id !== id) });
  }

  /** Loads a chapter chunk then notifies (screens re-render with content). */
  async ensureChapter(index: number): Promise<void> {
    if (this.content.getChapter(index)) return;
    await this.content.ensure(index);
    this.set({});
  }
}
